import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AptitudeTimer } from "@/components/aptitude/AptitudeTimer";
import { ChevronRight, Loader2, Brain } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

export default function AptitudeTest() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [itemIndex, setItemIndex] = useState(0);
  const [localAnswers, setLocalAnswers] = useState({});
  const [localFreeText, setLocalFreeText] = useState({});
  const [ideaDraft, setIdeaDraft] = useState("");
  const [timerToken, setTimerToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/student/aptitude/current");
      if (res.data.completed) {
        navigate("/aptitude/report");
        return;
      }
      setData(res.data);
      setItemIndex(0);
      setLocalAnswers({});
      setLocalFreeText({});
      setIdeaDraft("");
      setTimerToken((k) => k + 1);
    } catch {
      toast.error("Could not load the Aptitude Test. Please try again.");
      navigate("/aptitude");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadCurrent(); }, [loadCurrent]);

  const finishSubtest = async (answersPayload, freeTextPayload) => {
    setSubmitting(true);
    try {
      const res = await api.post("/student/aptitude/submit-subtest", { answers: answersPayload, free_text: freeTextPayload });
      if (res.data.completed) navigate("/aptitude/report");
      else loadCurrent();
    } catch {
      toast.error("Could not submit this section. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="aptitude-test-loading">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  const { subtest, items, index, total } = data;
  const item = items[itemIndex];
  const isIdeaGen = subtest.key === "idea_generation";
  const isLastItem = itemIndex === items.length - 1;
  const answered = localAnswers[item.id] !== undefined;

  const handleSubtestTimeUp = () => finishSubtest(localAnswers, localFreeText);

  const setAnswer = (value) => setLocalAnswers((prev) => ({ ...prev, [item.id]: value }));

  const advance = () => {
    if (isLastItem) finishSubtest(localAnswers, localFreeText);
    else setItemIndex((i) => i + 1);
  };

  const handleVcsAdvance = (value) => {
    const updated = { ...localAnswers, [item.id]: value };
    setLocalAnswers(updated);
    if (isLastItem) finishSubtest(updated, localFreeText);
    else setItemIndex((i) => i + 1);
  };

  const advanceIdeaItem = () => {
    const freeTextPayload = { ...localFreeText, [item.id]: ideaDraft };
    setLocalFreeText(freeTextPayload);
    if (isLastItem) {
      finishSubtest({}, freeTextPayload);
    } else {
      setItemIndex((i) => i + 1);
      setIdeaDraft("");
      setTimerToken((k) => k + 1);
    }
  };

  const optionClass = (selected) =>
    cn(
      "w-full text-left rounded-xl border px-4 py-3.5 text-sm transition-colors",
      selected ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-medium" : "border-slate-200 hover:border-indigo-300"
    );

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="aptitude-test-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <BrandLogo className="h-6" />
          <span className="text-xs font-mono text-slate-400" data-testid="aptitude-subtest-progress">Section {index + 1} / {total}</span>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
            <h1 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2" data-testid="aptitude-subtest-name">
              <Brain className="h-5 w-5 text-indigo-600" /> {subtest.name}
            </h1>
            {isIdeaGen ? (
              <AptitudeTimer key={`${item.id}-${timerToken}`} seconds={item.time_limit_seconds} onExpire={advanceIdeaItem} testId="aptitude-item-timer" />
            ) : (
              <AptitudeTimer key={`${subtest.key}-${timerToken}`} seconds={subtest.time_limit_seconds} onExpire={handleSubtestTimeUp} testId="aptitude-subtest-timer" />
            )}
          </div>
          <p className="text-xs text-slate-400 mb-6">{subtest.description}</p>
          <div className="mb-2 text-xs text-slate-400 font-mono" data-testid="aptitude-item-progress">Item {itemIndex + 1} of {items.length}</div>

          {item.type === "mcq" && (
            <div data-testid={`aptitude-item-${item.id}`}>
              <p className="text-base text-slate-800 leading-relaxed mb-5" data-testid="aptitude-item-prompt">{item.prompt}</p>
              <div className="space-y-2">
                {item.options.map((o) => (
                  <button key={o.id} type="button" data-testid={`aptitude-option-${item.id}-${o.id}`} onClick={() => setAnswer(o.id)} className={optionClass(localAnswers[item.id] === o.id)}>
                    <span className="font-mono text-xs text-slate-400 mr-2">{o.id}</span>{o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {item.type === "forced_choice" && (
            <div data-testid={`aptitude-item-${item.id}`}>
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-4">Choose the statement that feels more true for you</p>
              <div className="space-y-2">
                <button type="button" data-testid={`aptitude-option-${item.id}-A`} onClick={() => setAnswer("A")} className={optionClass(localAnswers[item.id] === "A")}>{item.option_a.label}</button>
                <button type="button" data-testid={`aptitude-option-${item.id}-B`} onClick={() => setAnswer("B")} className={optionClass(localAnswers[item.id] === "B")}>{item.option_b.label}</button>
              </div>
            </div>
          )}

          {item.type === "same_different" && (
            <div data-testid={`aptitude-item-${item.id}`} className="text-center py-6">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-6">Are these two the same or different?</p>
              <div className="flex items-center justify-center gap-6 font-mono text-2xl text-slate-800 mb-8">
                <span data-testid="aptitude-vcs-string-1">{item.pair[0]}</span>
                <span className="text-slate-300">vs</span>
                <span data-testid="aptitude-vcs-string-2">{item.pair[1]}</span>
              </div>
              <div className="flex items-center justify-center gap-4">
                <Button data-testid={`aptitude-vcs-same-${item.id}`} disabled={submitting} onClick={() => handleVcsAdvance("same")} className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-8">Same</Button>
                <Button data-testid={`aptitude-vcs-different-${item.id}`} disabled={submitting} onClick={() => handleVcsAdvance("different")} variant="outline" className="rounded-full px-8">Different</Button>
              </div>
            </div>
          )}

          {item.type === "free_text" && (
            <div data-testid={`aptitude-item-${item.id}`}>
              <p className="text-base text-slate-800 leading-relaxed mb-4" data-testid="aptitude-item-prompt">{item.prompt}</p>
              <Textarea data-testid={`aptitude-idea-textarea-${item.id}`} value={ideaDraft} onChange={(e) => setIdeaDraft(e.target.value)} placeholder="Type each idea separated by a comma or new line..." className="min-h-[160px]" />
            </div>
          )}

          {(item.type === "mcq" || item.type === "forced_choice") && (
            <div className="flex justify-end mt-8">
              <Button data-testid="aptitude-next-button" disabled={!answered || submitting} onClick={advance}>
                {isLastItem ? "Finish Section" : "Next"} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
          {item.type === "free_text" && (
            <div className="flex justify-end mt-8">
              <Button data-testid="aptitude-idea-next-button" disabled={submitting} onClick={advanceIdeaItem}>
                {isLastItem ? "Finish Section" : "Next Idea Prompt"} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
